import React from "react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Button } from "@/components/ui/Button";
import { Eye, ChevronUp, ChevronDown, Edit2 } from "lucide-react";
import {
  statusColors,
  enumCompatColors,
  columnsByType,
} from "./ComparisonTable.constants";
import type {
  ComparisonTableProps,
  TableColumn,
} from "./ComparisonTable.types";

function renderCell(
  row: Record<string, unknown>,
  column: TableColumn,
): React.ReactNode {
  const value = row[column.key];

  if (column.key === "status") {
    return <StatusBadge status={statusColors[String(value)] || "DISABLED"} />;
  }

  if (column.key === "enumCompat") {
    return (
      <StatusBadge
        status={enumCompatColors[String(value)] || "DISABLED"}
        showDot={false}
      />
    );
  }

  if (column.key === "confidence") {
    const percent = Math.round(Number(value) * 100);
    return (
      <div className="flex items-center gap-2">
        <div className="w-16 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div
            className={cn(
              "h-full rounded-full",
              percent >= 80
                ? "bg-emerald-500"
                : percent >= 50
                  ? "bg-amber-500"
                  : "bg-rose-500",
            )}
            style={{ width: `${percent}%` }}
          />
        </div>
        <span
          className={cn(
            "text-sm font-medium",
            percent >= 80
              ? "text-emerald-600 dark:text-emerald-400"
              : percent >= 50
                ? "text-amber-600 dark:text-amber-400"
                : "text-rose-600 dark:text-rose-400",
          )}
        >
          {percent}%
        </span>
      </div>
    );
  }

  if (column.key === "matchSignals") {
    const arr = value as string[] | undefined;
    return (
      <div className="flex items-center gap-1">
        {arr?.length ? (
          arr.map((signal: string, i: number) => (
            <span
              key={i}
              className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400"
            >
              {signal}
            </span>
          ))
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </div>
    );
  }

  if (value === null || value === undefined) {
    return <span className="text-slate-400 dark:text-slate-500">—</span>;
  }

  return (
    <span className="text-slate-700 dark:text-slate-300">{String(value)}</span>
  );
}

export const ComparisonTable: React.FC<ComparisonTableProps> = ({
  type = "category",
  data = [],
  sortColumn,
  sortDirection,
  onSort,
  onReview,
  onEdit,
  className,
}) => {
  const currentColumns = columnsByType[type] ?? columnsByType.category;

  return (
    <div
      data-testid={`comparison-table-${type}`}
      className={cn(
        "overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700",
        className,
      )}
    >
      <table className="w-full">
        <thead>
          <tr className="bg-slate-50 dark:bg-slate-800">
            {currentColumns.map((col) => (
              <th
                key={col.key}
                onClick={() => col.sortable && onSort?.(col.key)}
                className={cn(
                  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider",
                  "text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700",
                  col.sortable &&
                    "cursor-pointer hover:text-slate-700 dark:hover:text-slate-300",
                )}
              >
                <div className="flex items-center gap-1">
                  {col.label}
                  {col.sortable &&
                    sortColumn === col.key &&
                    (sortDirection === "asc" ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    ))}
                </div>
              </th>
            ))}
            <th className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 w-24" />
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => (
            <tr
              key={String((row as Record<string, unknown>).id ?? rowIndex)}
              data-testid={`table-row-${rowIndex}`}
              className={cn(
                "border-b border-slate-100 dark:border-slate-800 last:border-0",
                "bg-white dark:bg-slate-900",
                "hover:bg-slate-50 dark:hover:bg-slate-800/50",
              )}
            >
              {currentColumns.map((col) => (
                <td key={col.key} className="px-4 py-3 text-sm">
                  {renderCell(row as Record<string, unknown>, col)}
                </td>
              ))}
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  {onReview && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onReview(row as Record<string, unknown>)}
                      data-testid={`review-btn-${rowIndex}`}
                      aria-label="Review mapping"
                      title="Review mapping"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  )}
                  {onEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(row as Record<string, unknown>)}
                      data-testid={`edit-btn-${rowIndex}`}
                      aria-label="Edit mapping"
                      title="Edit mapping"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
